import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    const { data } = event.detail;
    // The event data does not always contain 'product' (it contains 'html', 'productId', 'newProduct').
    // We shouldn't return early just because 'product' is missing, as we need to update the price HTML.

    const closestSection = this.closest('.shopify-section');
    const isPerMeterInput = closestSection ? closestSection.querySelector('#is-per-meter') : document.getElementById('is-per-meter');
    const isPerMeter = isPerMeterInput && isPerMeterInput.value === '1';

    let newPriceParent;
    if (this.dataset.blockId) {
      newPriceParent = data.html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
    }

    // Fallback: If specific block not found, try generic one
    if (!newPriceParent) {
      newPriceParent = data.html.querySelector('product-price');
    }

    const newPrice = newPriceParent ? newPriceParent.querySelector('[ref="priceContainer"]') : null;
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    console.log('ProductPrice Update:', {
      blockId: this.dataset.blockId,
      foundNewParent: !!newPriceParent,
      newPriceText: newPrice ? newPrice.innerText.trim() : null,
      currentPriceText: currentPrice ? currentPrice.innerText.trim() : null,
      fetchUrl: data.url // if available
    });

    if (!newPrice || !currentPrice) {
      console.warn('ProductPrice: Elements not found', { newPrice, currentPrice });
      return;
    }

    // If no change in HTML, skip
    // console.log('Old vs New HTML:', currentPrice.innerHTML === newPrice.innerHTML);
    if (currentPrice.innerHTML === newPrice.innerHTML) return;

    // --- Custom Logic: Apply PM adjustments before replacing ---
    // Note: Liquid already handles the multiplication, so this JS logic might be redundant.
    // However, keeping it if the AJAX response somehow misses the PM tag logic?
    // Actually, let's trust the Liquid first. If Liquid works, newPrice should already have correct values.
    // If we re-multiply, it might be wrong.
    // But for now, let's see if we even reach here.

    if (isPerMeter) {
      // Robust Fix: Use the price from the event data directly if available, 
      // as server-side HTML rendering might be stale or incorrect.
      const resource = event.detail.resource;
      const priceElems = currentPrice.querySelectorAll('.price, .compare-at-price');

      if (resource && resource.price !== undefined) {
        // Calculate doubled price from the raw cent value
        const newAmount = (resource.price / 100) * 2;

        priceElems.forEach(el => {
          // We assume the element has some text content to steal the currency symbol from
          const currentText = el.textContent.trim();

          // Improved currency symbol extraction:
          // 1. Remove digits, dots, commas, spaces.
          // 2. Also remove letters (to get rid of "/meter" text).
          // 3. Remove forward slash (to get rid of "/")
          // 4. What's left should be the symbol (e.g. ₹, $, etc.)
          const currencySymbol = currentText.replace(/[0-9.,\sA-Za-z\/]/g, '') || '₹';

          // Verify if this element is a "compare at" price or regular price
          // The event only gives us 'price' (sale price) and 'compare_at_price'.
          // We need to be careful not to overwrite compare_at with regular price.

          if (el.classList.contains('compare-at-price') && resource.compare_at_price) {
            const newCompareAmount = (resource.compare_at_price / 100) * 2;
            el.textContent = `${currencySymbol}${newCompareAmount.toFixed(2)}/meter`;
            el.classList.remove('hidden'); // Ensure it's visible if it was hidden
          } else if (el.classList.contains('price') || el.classList.contains('price-item--sale') || el.classList.contains('price-item--regular')) {
            // This is likely the main price
            el.textContent = `${currencySymbol}${newAmount.toFixed(2)}/meter`;
          }
        });
      } else {
        // Fallback to scraping from HTML if resource is missing (unlikely)
        const newPriceElems = newPrice.querySelectorAll('.price, .compare-at-price');
        // Start with cloning new content
        currentPrice.innerHTML = newPrice.innerHTML;

        // Re-query within the updated DOM
        const updatedPriceElems = currentPrice.querySelectorAll('.price, .compare-at-price');
        updatedPriceElems.forEach(el => {
          const moneyText = el.textContent.trim();
          if (moneyText.includes('/meter')) return;
          const amount = parseFloat(moneyText.replace(/[^0-9.]/g, ''));
          if (!isNaN(amount)) {
            const currencySymbol = moneyText.replace(/[0-9.,\s]/g, '');
            const newAmount = amount * 2;
            el.textContent = `${currencySymbol}${newAmount.toFixed(2)}/meter`;
          }
        });
      }

      // Handle quantity display
      const qtyEl = this.closest('[ref="product"]')?.querySelector('[ref="quantityInput"]');
      if (qtyEl && qtyEl.value) {
        const originalQty = parseFloat(qtyEl.value);
        if (!isNaN(originalQty)) {
          // If we are "per meter", the qty input usually shows 1.0 but internal is 0.5?
          // Actually logic in other files handles qty. 
          // Just ensure we don't break it. 
          // The original code was dividing by 2. Let's keep it safe or remove if redundant.
          // Given the complexity, let's strictly focus on price update.
        }
      }
      return; // Stop here, we handled the update manually
    }

    // Replace HTML for non-pm products
    currentPrice.replaceWith(newPrice);
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
